import type { ProductDetail } from "@elektr/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { ImagesManager } from "@/components/catalog/ImagesManager";
import { Button } from "@/components/ui/button";
import { Card, PageHeader, Spinner } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ui/dialog";
import { Checkbox, Field, Input, Select, Textarea } from "@/components/ui/form";
import { useToast } from "@/components/ui/toast";
import { api, errorMessage, isConflict, unwrap } from "@/lib/api";
import { isAdmin, useAuth } from "@/lib/auth";
import { QUALITY_KEYS } from "@/lib/catalog";
import { useT } from "@/lib/i18n";

type Quality = keyof typeof QUALITY_KEYS;
interface SpecRow {
  key: string;
  value: string;
}
interface Form {
  name: string;
  sku: string;
  brand: string;
  category: string;
  price: string;
  oldPrice: string;
  quality: Quality;
  warranty: string;
  inStock: boolean;
  active: boolean;
  purpose: string;
  description: string;
  specs: SpecRow[];
  electrician: string;
}

const initial = (p?: ProductDetail): Form => ({
  name: p?.name ?? "",
  sku: p?.sku ?? "",
  brand: p?.brand ?? "",
  category: p?.category ?? "",
  price: p ? String(p.price_uzs) : "",
  oldPrice: p?.old_price_uzs ? String(p.old_price_uzs) : "",
  quality: (p?.quality ?? "standard") as Quality,
  warranty: p?.warranty_months ? String(p.warranty_months) : "",
  inStock: p?.in_stock ?? true,
  active: p?.is_active ?? true,
  purpose: p?.purpose ?? "",
  description: p?.description ?? "",
  specs: Object.entries((p?.specs ?? {}) as Record<string, string | number>).map(
    ([key, value]) => ({ key, value: String(value) }),
  ),
  electrician: p?.electrician ?? "",
});

/** Mahsulot yaratish/tahrirlash (xodimlar). Rasmlar mahsulot yaratilgandan keyin qo'shiladi. */
export function ProductFormPage() {
  const { id } = useParams();
  const { t } = useT();
  const query = useQuery({
    queryKey: ["product", id],
    enabled: !!id,
    queryFn: async () =>
      unwrap(await api.GET("/api/products/{id}/", { params: { path: { id: id! } } })),
    staleTime: 0,
  });
  if (id && query.isLoading) return <Spinner className="m-10 size-8" />;
  if (id && (query.isError || !query.data))
    return <p className="p-8 text-center text-muted-foreground">{t("catalog.not_found")}</p>;
  return <ProductForm key={`${id}:${query.data?.version}`} product={query.data} />;
}

function ProductForm({ product }: { product?: ProductDetail }) {
  const { t } = useT();
  const { user } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const nav = useNavigate();
  const admin = isAdmin(user);
  const [f, setF] = useState<Form>(() => initial(product));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [conflict, setConflict] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setF((s) => ({ ...s, [k]: v }));

  const categories = useQuery({
    queryKey: ["categories"],
    queryFn: async () => unwrap(await api.GET("/api/categories/")),
  });
  const electricians = useQuery({
    queryKey: ["users", "electrician", "all"],
    enabled: admin,
    queryFn: async () =>
      unwrap(
        await api.GET("/api/users/", {
          params: { query: { role: "electrician", page_size: 100 } },
        }),
      ),
  });

  const save = useMutation({
    mutationFn: async () => {
      const specs: Record<string, string> = {};
      for (const { key, value } of f.specs) if (key.trim()) specs[key.trim()] = value.trim();
      const body = {
        name: f.name.trim(),
        sku: f.sku.trim(),
        brand: f.brand.trim(),
        category: f.category || null,
        price_uzs: Number(f.price),
        old_price_uzs: f.oldPrice ? Number(f.oldPrice) : null,
        quality: f.quality,
        warranty_months: f.warranty ? Number(f.warranty) : null,
        in_stock: f.inStock,
        is_active: f.active,
        purpose: f.purpose,
        description: f.description,
        specs,
        ...(admin ? { electrician: f.electrician || null } : {}),
      };
      return product
        ? unwrap(
            await api.PATCH("/api/products/{id}/", {
              params: { path: { id: product.id } },
              body: { ...body, version: product.version },
            }),
          )
        : unwrap(await api.POST("/api/products/", { body }));
    },
    onSuccess: (saved) => {
      setConflict(false);
      setErrors({});
      toast.success(t("common.saved"));
      void qc.invalidateQueries({ queryKey: ["catalog"] });
      void qc.invalidateQueries({ queryKey: ["brands"] });
      qc.setQueryData(["product", saved.id], saved);
      if (!product) nav(`/catalog/${saved.id}/edit`, { replace: true });
    },
    onError: (e) => {
      if (isConflict(e)) return setConflict(true);
      const body = (e as { body?: Record<string, unknown> }).body;
      if (body && typeof body === "object") {
        const fieldErrors: Record<string, string> = {};
        for (const [k, v] of Object.entries(body))
          fieldErrors[k] = Array.isArray(v) ? v.join(", ") : String(v);
        if (Object.keys(fieldErrors).length) return setErrors(fieldErrors);
      }
      toast.error(errorMessage(e, t("common.error")));
    },
  });

  const remove = useMutation({
    mutationFn: async () => {
      const res = await api.DELETE("/api/products/{id}/", {
        params: { path: { id: product!.id } },
      });
      if (!res.response.ok) throw new Error("delete");
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["catalog"] });
      nav("/catalog", { replace: true });
    },
    onError: (e) => toast.error(errorMessage(e, t("common.error"))),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };
  const valid = f.name.trim() && f.price !== "" && Number(f.price) >= 0;

  return (
    <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
      <PageHeader
        title={product ? t("catalog.edit_product") : t("catalog.new")}
        actions={
          product && (
            <>
              <Button asChild variant="outline">
                <Link to={`/catalog/${product.id}`}>{t("catalog.view")}</Link>
              </Button>
              <Button variant="outline" onClick={() => setConfirmDelete(true)}>
                <Trash2 className="size-4" /> {t("common.delete")}
              </Button>
            </>
          )
        }
      />
      {conflict && (
        <Card role="alert" className="border-warning bg-warning/10">
          <p className="mb-2 text-sm font-medium">{t("common.conflict")}</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void qc.invalidateQueries({ queryKey: ["product", product?.id] })}
          >
            {t("common.reload")}
          </Button>
        </Card>
      )}

      <Card className="grid gap-4 md:grid-cols-2">
        <Field label={t("catalog.name")} error={errors.name} className="md:col-span-2">
          {(id) => (
            <Input id={id} required value={f.name} onChange={(e) => set("name", e.target.value)} />
          )}
        </Field>
        <Field label={t("catalog.sku")} error={errors.sku} hint={t("catalog.sku_hint")}>
          {(id) => <Input id={id} value={f.sku} onChange={(e) => set("sku", e.target.value)} />}
        </Field>
        <Field label={t("catalog.brand")} error={errors.brand}>
          {(id) => <Input id={id} value={f.brand} onChange={(e) => set("brand", e.target.value)} />}
        </Field>
        <Field label={t("catalog.category")} error={errors.category}>
          {(id) => (
            <Select id={id} value={f.category} onChange={(e) => set("category", e.target.value)}>
              <option value="">—</option>
              {categories.data?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={t("catalog.quality")} error={errors.quality}>
          {(id) => (
            <Select
              id={id}
              value={f.quality}
              onChange={(e) => set("quality", e.target.value as Quality)}
            >
              {(Object.keys(QUALITY_KEYS) as Quality[]).map((q) => (
                <option key={q} value={q}>
                  {t(QUALITY_KEYS[q])}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={t("catalog.price")} error={errors.price_uzs}>
          {(id) => (
            <Input
              id={id}
              type="number"
              inputMode="numeric"
              min={0}
              value={f.price}
              onChange={(e) => set("price", e.target.value)}
            />
          )}
        </Field>
        <Field
          label={t("catalog.old_price")}
          error={errors.old_price_uzs}
          hint={t("catalog.old_price_hint")}
        >
          {(id) => (
            <Input
              id={id}
              type="number"
              inputMode="numeric"
              min={0}
              value={f.oldPrice}
              onChange={(e) => set("oldPrice", e.target.value)}
            />
          )}
        </Field>
        <Field label={t("catalog.warranty_months")} error={errors.warranty_months}>
          {(id) => (
            <Input
              id={id}
              type="number"
              inputMode="numeric"
              min={0}
              value={f.warranty}
              onChange={(e) => set("warranty", e.target.value)}
            />
          )}
        </Field>
        {admin && (
          <Field
            label={t("catalog.offered_by")}
            hint={t("catalog.offered_by_hint")}
            error={errors.electrician}
          >
            {(id) => (
              <Select
                id={id}
                value={f.electrician}
                onChange={(e) => set("electrician", e.target.value)}
              >
                <option value="">{t("catalog.company_itself")}</option>
                {electricians.data?.results.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.full_name || u.phone}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}
        <Field label={t("catalog.purpose")} className="md:col-span-2">
          {(id) => (
            <Textarea
              id={id}
              rows={2}
              value={f.purpose}
              onChange={(e) => set("purpose", e.target.value)}
            />
          )}
        </Field>
        <Field label={t("catalog.description")} className="md:col-span-2">
          {(id) => (
            <Textarea
              id={id}
              rows={4}
              value={f.description}
              onChange={(e) => set("description", e.target.value)}
            />
          )}
        </Field>
        <Checkbox
          label={t("catalog.in_stock")}
          checked={f.inStock}
          onChange={(e) => set("inStock", e.target.checked)}
        />
        <Checkbox
          label={t("catalog.active")}
          checked={f.active}
          onChange={(e) => set("active", e.target.checked)}
        />
      </Card>

      <Card>
        <h3 className="mb-1 font-semibold">{t("catalog.specs")}</h3>
        <p className="mb-3 text-xs text-muted-foreground">{t("catalog.specs_hint")}</p>
        {errors.specs && (
          <p role="alert" className="mb-2 text-sm text-danger">
            {errors.specs}
          </p>
        )}
        <div className="flex flex-col gap-2">
          {f.specs.map((row, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2">
              <Input
                aria-label={`${t("catalog.spec_name")} ${i + 1}`}
                placeholder={t("catalog.spec_name")}
                value={row.key}
                onChange={(e) =>
                  set(
                    "specs",
                    f.specs.map((r, j) => (j === i ? { ...r, key: e.target.value } : r)),
                  )
                }
              />
              <Input
                aria-label={`${t("catalog.spec_value")} ${i + 1}`}
                placeholder={t("catalog.spec_value")}
                value={row.value}
                onChange={(e) =>
                  set(
                    "specs",
                    f.specs.map((r, j) => (j === i ? { ...r, value: e.target.value } : r)),
                  )
                }
              />
              <Button
                variant="ghost"
                size="icon"
                aria-label={`${t("common.delete")} ${i + 1}`}
                onClick={() =>
                  set(
                    "specs",
                    f.specs.filter((_, j) => j !== i),
                  )
                }
              >
                <Trash2 className="size-4" />
              </Button>
            </div>
          ))}
        </div>
        <Button
          variant="outline"
          size="sm"
          className="mt-3"
          onClick={() => set("specs", [...f.specs, { key: "", value: "" }])}
        >
          <Plus className="size-4" /> {t("catalog.add_spec")}
        </Button>
      </Card>

      <div className="flex justify-end">
        <Button type="submit" disabled={save.isPending || !valid}>
          {t("common.save")}
        </Button>
      </div>

      <Card>
        <h3 className="mb-3 font-semibold">{t("catalog.photos")}</h3>
        {product ? (
          <ImagesManager product={product} />
        ) : (
          <p className="text-sm text-muted-foreground">{t("catalog.photos_after_save")}</p>
        )}
      </Card>

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={t("catalog.delete_product")}
        message={t("catalog.delete_product_message")}
        confirmLabel={t("common.delete")}
        pending={remove.isPending}
        onConfirm={() => remove.mutate()}
      />
    </form>
  );
}
